/**
 * 实时客户端（WebSocket）。
 *
 * 约束来源：CONTRACT.md 第 22 / 41 / 42 行与需求 9.3。
 * - 连接前用一次性短时 ticket，ticket 只作为查询参数，绝不放进日志；
 * - 连上后**先等 ready**，再执行历史/列表对齐，避免 history-before-subscribe 窗口；
 * - ready 之后任何 notify 只做"标脏"，由合并补拉循环排空到干净；
 * - 每次重连都重新取 ticket，并对所有活跃游标重新对齐；
 * - 事件可能重复，消息去重交由 {@link MessageStore} 按 Message.id 完成；
 * - auth.expired 不自动重连，交回上层重新换取凭据；
 * - draining（滚动发布）按退避重连。
 */

import type { BackoffOptions, SafeLogger } from '@support/platform';
import { Backoff, buildRealtimeUrl, createSafeLogger, redactUrl } from '@support/platform';

export type RealtimeStatus = 'connected' | 'connecting' | 'idle' | 'stopped';

export type RealtimeEventType =
  | 'auth.expired'
  | 'draining'
  | 'ready'
  | 'sync.required';

const KNOWN_TYPES: ReadonlySet<string> = new Set<RealtimeEventType>([
  'auth.expired',
  'draining',
  'ready',
  'sync.required',
]);

export interface RealtimeEvent {
  connectionId?: string;
  conversationId?: string;
  eventId?: string;
  type: RealtimeEventType;
  version: string;
}

export interface RealtimeClientOptions {
  /** 实时基地址，例如 ws://127.0.0.1:28081/v1/support。 */
  url: string;
  /** 每次（重）连都必须重新获取一次性 ticket。 */
  fetchTicket: () => Promise<{ expiresAt: string; ticket: string }>;
  WebSocketImpl?: typeof WebSocket;
  backoffOptions?: BackoffOptions;
  logger?: SafeLogger;
  /** auth.expired 时回调；上层应重新 exchange 或退出登录。 */
  onAuthExpired?: () => void;
  /** 节点排空（滚动发布）。 */
  onDraining?: () => void;
  /** 握手完成（ready）后对齐历史/列表。 */
  onReady?: () => Promise<void> | void;
  /** 需要补拉（合并后调用）。 */
  onSyncRequired?: (reason: 'dirty') => Promise<void> | void;
  onStatusChange?: (status: RealtimeStatus) => void;
  /** 收到但无法处理的协议问题，用于诊断，不影响连接。 */
  onProtocolIssue?: (detail: string) => void;
}

export class RealtimeClient {
  private readonly options: RealtimeClientOptions;

  private readonly backoff: Backoff;

  private readonly logger: SafeLogger;

  private draining: null | Promise<void> = null;

  private dirty = false;

  private isReadyFlag = false;

  private reconnectTimer: null | ReturnType<typeof setTimeout> = null;

  private socket: null | WebSocket = null;

  private statusValue: RealtimeStatus = 'idle';

  private stopRequested = false;

  private readonly url: string;

  constructor(options: RealtimeClientOptions) {
    this.options = options;
    this.url = options.url;
    this.backoff = new Backoff(options.backoffOptions);
    this.logger =
      options.logger ??
      createSafeLogger({ enabled: false, scope: 'support-realtime' });
  }

  get status(): RealtimeStatus {
    return this.statusValue;
  }

  get isReady(): boolean {
    return this.isReadyFlag;
  }

  /** 供诊断使用：已脱敏的连接地址，绝不包含明文 ticket。 */
  redactedConnectionUrl(): string {
    return redactUrl(this.socket?.url ?? this.url);
  }

  /** 建立连接。失败会抛出（上层决定是否重试）。 */
  async start(): Promise<void> {
    this.stopRequested = false;
    await this.connectOnce();
  }

  stop(): void {
    this.stopRequested = true;
    this.isReadyFlag = false;
    this.dirty = false;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.socket;
    this.socket = null;
    this.setStatus('stopped');
    if (socket) {
      try {
        socket.close();
      } catch {
        // 关闭失败不影响状态收敛
      }
    }
  }

  /** 外部通知（例如本地乐观状态变化）时手工标脏。 */
  markDirty(): void {
    this.dirty = true;
    void this.drain();
  }

  private async connectOnce(): Promise<void> {
    if (this.stopRequested) return;
    this.setStatus('connecting');
    const { ticket } = await this.options.fetchTicket();
    if (this.stopRequested) return;
    const WebSocketImpl = this.options.WebSocketImpl ?? globalThis.WebSocket;
    if (typeof WebSocketImpl !== 'function') {
      throw new Error('当前环境没有可用的 WebSocket 实现');
    }
    const url = buildRealtimeUrl(this.url, ticket);
    const socket = new WebSocketImpl(url);
    this.socket = socket;
    this.logger.info('connecting', { url: redactUrl(url) });

    socket.onopen = () => {
      if (this.stopRequested) return;
      this.backoff.reset();
      this.setStatus('connected');
    };
    socket.onmessage = (event: { data: unknown }) => {
      void this.handleMessage(event.data);
    };
    socket.onerror = () => {
      this.logger.warn('socket error');
    };
    socket.onclose = () => {
      if (this.socket === socket) this.socket = null;
      this.handleClosed();
    };
  }

  private async handleMessage(raw: unknown): Promise<void> {
    if (typeof raw !== 'string') {
      this.issue('非文本帧被忽略');
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      this.issue('收到非 JSON 帧，已忽略');
      return;
    }
    const event = parseRealtimeEvent(parsed);
    if (!event) {
      // 非规范帧绝不触发认证/连接类副作用（auth.expired、draining、ready）。
      // 但 sync.required 只是"可能有变化"的提示：丢弃它会静默漏消息，
      // 因此即使缺少 version 也照常保守标脏——补拉本身可合并且幂等，
      // 多补一次的代价远小于漏一条客户的真实消息。
      if (isSyncRequiredHint(parsed)) {
        this.issue('sync.required 缺少必需字段，按保守标脏处理');
        this.dirty = true;
        await this.drain();
        return;
      }
      this.issue('收到不符合协议的事件，已忽略');
      return;
    }
    switch (event.type) {
      case 'auth.expired': {
        this.isReadyFlag = false;
        this.stop();
        this.options.onAuthExpired?.();
        return;
      }
      case 'draining': {
        this.options.onDraining?.();
        this.isReadyFlag = false;
        this.closeCurrent();
        this.scheduleReconnect();
        return;
      }
      case 'ready': {
        this.isReadyFlag = true;
        try {
          await this.options.onReady?.();
        } catch (error) {
          this.logger.warn('ready 对齐失败', { error: String(error) });
          this.dirty = true;
        }
        // ready 之前累积的脏标记在这里兑现，保证不漏消息
        await this.drain();
        return;
      }
      case 'sync.required': {
        this.dirty = true;
        await this.drain();
        return;
      }
      default: {
        this.issue(`未知事件类型 ${String((event as { type: string }).type)}`);
        return;
      }
    }
  }

  /**
   * 合并补拉循环：处理期间到达的多次 notify 只再补拉一次，
   * 直到标记干净为止（不会 1:1 放大成 N 次补拉）。
   *
   * `draining` 必须在补拉真正开始**之前**落位：若先启动 async 体再赋值，
   * 无脏标记时循环会同步结束并把 null 写回，留下永久的"在飞"假象——
   * 之后所有 notify 都会被合并守卫吞掉，脏标记再也排不空（静默漏消息）。
   */
  private drain(): Promise<void> {
    if (!this.isReadyFlag) return Promise.resolve(); // ready 之前只累积标记
    if (this.draining) return this.draining; // 已在补拉：由在飞循环继续消费标记
    const handle = createDeferred();
    this.draining = handle.promise;
    void (async () => {
      try {
        while (this.dirty && !this.stopRequested && this.isReadyFlag) {
          this.dirty = false;
          await this.options.onSyncRequired?.('dirty');
        }
      } catch (error) {
        this.dirty = true; // 补拉失败保留标记，等待下次 ready/notify
        this.logger.warn('补拉失败，保留脏标记', { error: String(error) });
      } finally {
        this.draining = null;
        handle.resolve();
      }
    })();
    return handle.promise;
  }

  private handleClosed(): void {
    this.isReadyFlag = false;
    if (this.stopRequested) {
      this.setStatus('stopped');
      return;
    }
    this.scheduleReconnect();
  }

  private closeCurrent(): void {
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      try {
        socket.close();
      } catch {
        // ignore
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.stopRequested || this.reconnectTimer !== null) return;
    const delay = this.backoff.next();
    this.setStatus('connecting');
    this.logger.info('计划重连', { delayMs: delay, attempt: this.backoff.attempts });
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connectOnce().catch((error) => {
        this.logger.warn('重连失败，继续退避', { error: String(error) });
        this.scheduleReconnect();
      });
    }, delay);
  }

  private setStatus(status: RealtimeStatus): void {
    if (this.statusValue === status) return;
    this.statusValue = status;
    this.options.onStatusChange?.(status);
  }

  private issue(detail: string): void {
    this.logger.debug('协议问题', { detail });
    this.options.onProtocolIssue?.(detail);
  }
}

interface Deferred {
  promise: Promise<void>;
  resolve: () => void;
}

/** 手工可控的 promise：让"在飞中"标记在异步工作开始前就已落位。 */
function createDeferred(): Deferred {
  let resolve: () => void = () => {};
  const promise = new Promise<void>((r) => {
    resolve = r;
  });
  return { promise, resolve };
}

/**
 * 是否为"声明需要重新同步"的通知帧。
 *
 * 只看 type 字段：sync.required 是无状态的补拉提示，缺字段时仍按脏处理，
 * 避免因为一个不规范的帧而静默漏掉真实消息变化。
 */
function isSyncRequiredHint(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  return (value as Record<string, unknown>).type === 'sync.required';
}

/** 校验 WS 事件结构；不符合协议返回 null（调用方忽略而不是崩溃）。 */
export function parseRealtimeEvent(value: unknown): null | RealtimeEvent {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.type !== 'string' || !KNOWN_TYPES.has(record.type)) return null;
  if (typeof record.version !== 'string' || record.version === '') return null;
  const event: RealtimeEvent = {
    type: record.type as RealtimeEventType,
    version: record.version,
  };
  if (typeof record.eventId === 'string') event.eventId = record.eventId;
  if (typeof record.conversationId === 'string') {
    event.conversationId = record.conversationId;
  }
  if (typeof record.connectionId === 'string') {
    event.connectionId = record.connectionId;
  }
  return event;
}